import React, { useMemo } from "react";

import { Link, useLocation } from "@remix-run/react";
import {
    Activity,
    Circle,
    CircleDot,
    Dot,
    FileText,
    Filter,
    MessageSquare,
    Mic,
    Plus,
    Shield,
    SortAsc,
    SortDesc,
    Sprout,
    Upload,
    UserPlus,
    Users,
    Video,
    CheckSquare,
} from "lucide-react";

// Add SessionsHeader component
import { Button } from "~/components/ui/button";
import { A, H2 } from "~/components/ui/typography";
import { UserMenu } from "~/components/user-menu";
import { useUserRelationships } from "~/hooks/queries/use-user-relationships";
import { useAccountFeatureFlags } from "~/hooks/use-account-feature-flags";
import { useAnonymous } from "~/hooks/use-anonymous";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { cn } from "~/lib/utils";
import { SUPPORT_EMAIL } from "~/lib/env.shared";

import { useIsPractitioner } from "../lib/user";

interface NavItemProps {
    href: string;
    isActive: boolean;
    icon: React.ReactNode;
    text: string;
}

const NavItem = ({ href, isActive, icon, text }: NavItemProps) => (
    <Link
        to={href}
        className={cn(
            "flex items-center py-3 justify-end pr-6",
            isActive ? "font-semibold" : "",
        )}
    >
        <div className="flex items-center justify-end">
            <span className="mr-2">{text}</span>
            <div className="size-5">{icon}</div>
        </div>
    </Link>
);

type SidebarNavProps = React.HTMLAttributes<HTMLDivElement>;

export function SidebarNav({ className, ...props }: SidebarNavProps) {
    const location = useLocation();
    const { isPractitioner } = useIsPractitioner();
    const { isAnonymous } = useAnonymous();
    const { data: featureFlags } = useAccountFeatureFlags();
    const isAdminRoute = location.pathname.startsWith("/admin");

    const items = useMemo(() => {
        // If we're on an admin route, show admin navigation
        if (isAdminRoute) {
            return [
                {
                    href: "/admin",
                    icon: <Shield className="size-full" />,
                    text: "Dashboard",
                },
                {
                    href: "/admin/practitioners",
                    icon: <Users className="size-full" />,
                    text: "Practitioners",
                },
                {
                    href: "/admin/feedback",
                    icon: <MessageSquare className="size-full" />,
                    text: "Feedback",
                },
                {
                    href: "/admin/fireflies",
                    icon: <Mic className="size-full" />,
                    text: "Fireflies",
                },
                {
                    href: "/admin/event-dispatcher",
                    icon: <Activity className="size-full" />,
                    text: "Event Dispatcher",
                },
            ];
        }

        // Regular navigation for non-admin routes
        const baseItems = [
            {
                href: "/invite",
                icon: <Video className="size-full" />,
                text: "Invite to Session",
            },
            {
                href: "/upload",
                icon: <Upload className="size-full" />,
                text: "Upload Recording",
            },
        ];

        if (isPractitioner) {
            // AIDEV-NOTE: Show My Parts menu item only if feature flag is enabled
            baseItems.unshift({
                href: "/parts",
                icon: <Sprout className="size-full" />,
                text: "My Parts",
            });
            baseItems.unshift({
                href: "/practitioner/sessions",
                icon: <FileText className="size-full" />,
                text: "Sessions",
            });
            baseItems.unshift({
                href: "/practitioner",
                icon: <Users className="size-full" />,
                text: "Home",
            });
            baseItems.push({
                href: "/practitioner/invite",
                icon: <UserPlus className="size-full" />,
                text: "Invite a Client",
            });
        } else {
            baseItems.unshift({
                href: "/sessions",
                icon: <FileText className="size-full" />,
                text: "Sessions",
            });
            // AIDEV-NOTE: Show Parts menu item only if feature flag is enabled
            baseItems.unshift({
                href: "/parts",
                icon: <Sprout className="size-full" />,
                text: "Parts",
            });

            // Only show commitments if feature flag is enabled
            if (featureFlags?.enableCommitments) {
                baseItems.unshift({
                    href: "/commitments",
                    icon: <CheckSquare className="size-full" />,
                    text: "Commitments",
                });
            }
            // AIDEV-NOTE: Only add conditional items after hydration to avoid mismatches
            if (!isAnonymous) {
                baseItems.push({
                    href: "/client/invite-practitioner",
                    icon: <UserPlus className="size-full" />,
                    text: "Invite Practitioner",
                });
            }
        }

        return baseItems;
    }, [
        isPractitioner,
        isAnonymous,
        isAdminRoute,
        featureFlags?.enableCommitments,
    ]);

    const logoLink = isAdminRoute
        ? "/admin"
        : isPractitioner
          ? "/practitioner"
          : "/sessions";
    return (
        <div
            className={cn(
                "border-r min-h-screen bg-background relative transition-all duration-300 opacity-50 hover:opacity-100 text-rose-950 hover:text-muted-foreground 2xl:min-w-[20%] w-72 md:pl-4",
                "sticky top-8 z-30",
                className,
            )}
            {...props}
        >
            <div className="sticky top-0 flex h-screen flex-col space-y-6 py-4 lg:mt-[2.0rem] lg:pt-8">
                <div className="flex items-center justify-end px-3 py-2 pr-6">
                    <Link
                        to={logoLink}
                        className="flex items-center font-display text-3xl font-bold leading-none"
                    >
                        {PRODUCT_NAME}
                    </Link>
                </div>

                <div className="flex-1 space-y-1.5 px-3">
                    {items.map((item) => (
                        <NavItem
                            key={item.href}
                            href={item.href}
                            isActive={location.pathname === item.href}
                            icon={item.icon}
                            text={item.text}
                        />
                    ))}
                </div>

                <div className="flex flex-col space-y-2 pb-4">
                    {/* User section at the bottom */}
                    <div className="flex justify-end border-t px-3 pb-6 pr-4 pt-8">
                        <UserMenu />
                    </div>

                    {/* Terms, Privacy, and Support links */}
                    <div className="px-3 pr-6 text-xs text-muted-foreground">
                        <div className="flex items-center justify-end space-x-2">
                            <A
                                href="/terms"
                                className="transition-colors hover:text-rose-500"
                            >
                                Terms
                            </A>
                            <Dot className="size-4" />
                            <A
                                href="/privacy"
                                className="transition-colors hover:text-rose-500"
                            >
                                Privacy
                            </A>
                            <Dot className="size-4" />
                            <A
                                href={`mailto:${SUPPORT_EMAIL}`}
                                className="transition-colors hover:text-rose-500"
                            >
                                Support
                            </A>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Add SessionsHeader component definition
export interface SessionsHeaderProps {
    selectedDate: Date;
    onDateChange: (date: Date) => void;
    onNewSession: () => void;
    onFilterToggle: () => void;
    onSortChange: () => void;
    sortDirection: "asc" | "desc";
}

export function SessionsHeader({
    selectedDate,
    onDateChange,
    onNewSession,
    onFilterToggle,
    onSortChange,
    sortDirection,
}: SessionsHeaderProps) {
    return (
        <div className="mb-6">
            {/* For desktop: date picker and actions in the same row */}
            <div className="hidden items-center justify-between md:flex">
                <div>
                    <H2 className="mb-2 text-xl font-semibold">Select Date</H2>
                    <div className="w-[240px] rounded-md border p-2">
                        {selectedDate
                            ? selectedDate.toLocaleDateString()
                            : "Pick a date"}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        onClick={onNewSession}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Plus className="size-4" />
                        <span>New Session</span>
                    </Button>

                    <Button
                        onClick={onFilterToggle}
                        variant="outline"
                        size="icon"
                    >
                        <Filter className="size-4" />
                        <span className="sr-only">Filter sessions</span>
                    </Button>

                    <Button
                        onClick={onSortChange}
                        variant="outline"
                        size="icon"
                    >
                        {sortDirection === "asc" ? (
                            <SortAsc className="size-4" />
                        ) : (
                            <SortDesc className="size-4" />
                        )}
                        <span className="sr-only">Sort sessions</span>
                    </Button>
                </div>
            </div>

            <div className="flex flex-col items-center justify-between md:hidden">
                <H2 className="mb-2 text-xl font-semibold">Select Date</H2>
                <div className="mb-4">
                    <div className="w-full rounded-md border p-2">
                        {selectedDate
                            ? selectedDate.toLocaleDateString()
                            : "Pick a date"}
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button
                        onClick={onNewSession}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Plus className="size-4" />
                        <span>New Session</span>
                    </Button>

                    <Button
                        onClick={onFilterToggle}
                        variant="outline"
                        size="icon"
                    >
                        <Filter className="size-4" />
                        <span className="sr-only">Filter sessions</span>
                    </Button>

                    <Button
                        onClick={onSortChange}
                        variant="outline"
                        size="icon"
                    >
                        {sortDirection === "asc" ? (
                            <SortAsc className="size-4" />
                        ) : (
                            <SortDesc className="size-4" />
                        )}
                        <span className="sr-only">Sort sessions</span>
                    </Button>
                </div>
            </div>
        </div>
    );
}
